class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlay();
    this.setHeaderCartIconAccessibility();
  }

  bindOverlay() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (!overlay || overlay.dataset.cartDrawerReady === 'true') return;
    overlay.dataset.cartDrawerReady = 'true';
    overlay.addEventListener('click', this.close.bind(this));
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.cartDrawerReady === 'true') return;
    cartLink.dataset.cartDrawerReady = 'true';
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink)
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      document.dispatchEvent(new CustomEvent('cart:open'));
    });

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if(cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (!parsedState || !parsedState.sections) return false;

    const drawerInner = this.querySelector('.drawer__inner');
    if (drawerInner) drawerInner.classList.remove('is-empty');
    this.productId = parsedState.id;
    let drawerRendered = false;

    this.getSectionsToRender().forEach((section => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      const sectionHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (!sectionElement || sectionHTML === null) return;
      sectionElement.innerHTML = sectionHTML;
      if (section.id === 'cart-drawer') drawerRendered = true;
    }));

    if (!drawerRendered) return false;

    if (typeof parsedState.item_count === 'number') {
      this.classList.toggle('is-empty', parsedState.item_count === 0);
    } else {
      this.classList.toggle('is-empty', !this.querySelector('.cart-item'));
    }

    if (typeof window.SiamkickCurrencyRefresh === 'function') {
      window.SiamkickCurrencyRefresh(this);
    }

    setTimeout(() => {
      this.bindOverlay();
      this.setHeaderCartIconAccessibility();
      document.dispatchEvent(new CustomEvent('cart:drawer-rendered'));
      this.open();
    });

    return true;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (typeof html !== 'string') return null;
    const source = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
    return source ? source.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer'
      },
      {
        id: 'cart-icon-bubble'
      }
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  refreshAbortController = undefined;

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  onCartUpdate() {
    if (this.refreshAbortController) this.refreshAbortController.abort();
    this.refreshAbortController = new AbortController();
    const signal = this.refreshAbortController.signal;

    fetch(`${routes.cart_url}?section_id=cart-drawer`, { signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart drawer refresh failed (${response.status})`);
        return response.text();
      })
      .then((responseText) => {
        if (signal.aborted || !this.isConnected) return;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const cartDrawer = document.querySelector('cart-drawer');
        const sourceDrawer = html.querySelector('cart-drawer');
        const currentContents = document.getElementById('CartDrawer');
        const sourceContents = html.getElementById('CartDrawer');
        if (!cartDrawer || !sourceDrawer || !currentContents || !sourceContents) {
          throw new Error('Cart drawer refresh returned incomplete markup');
        }

        currentContents.innerHTML = sourceContents.innerHTML;
        cartDrawer.classList.toggle('is-empty', sourceDrawer.classList.contains('is-empty'));
        cartDrawer.bindOverlay();
        cartDrawer.setHeaderCartIconAccessibility();
        if (typeof window.SiamkickCurrencyRefresh === 'function') {
          window.SiamkickCurrencyRefresh(cartDrawer);
        }
        document.dispatchEvent(new CustomEvent('cart:drawer-rendered'));
      })
      .catch((error) => {
        if (error.name !== 'AbortError') console.error(error);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
